/**
 * routeComponents.js — makes build-time prerendering and browser hydration agree.
 *
 * App.jsx code-splits every route except the homepage, so a plain server render
 * would hit each React.lazy() boundary, suspend, and emit the blank <PageFallback/>
 * instead of the page's copy — which is exactly the content Google needs to see.
 * And if the server DID emit real markup while the browser rendered a fallback,
 * the two trees would disagree and hydration would blow the prerendered HTML away.
 *
 * Both problems are solved by one registry. A route's component is resolved BEFORE
 * the tree renders and parked here; App.jsx prefers a registered component over the
 * lazy one, so no Suspense boundary is ever reached on the first render:
 *
 *   • build (scripts/prerender.mjs → entry-ssg.jsx) registers every route eagerly.
 *   • browser (main.jsx) awaits only the current route's chunk, then hydrates.
 *
 * Same component on both sides ⇒ matching markup ⇒ hydration keeps the prerendered
 * DOM. Routes with no loader (the homepage, whose Landing is a static import; and
 * the app-shell routes) simply return null here and take App.jsx's normal path.
 */
import { LOADERS as MARKETING_LOADERS } from './components/marketing/routes.js';

// App.jsx's top-level code-split routes. The homepage is absent on purpose:
// Landing is a static import, so it already renders on both sides.
const CORE_LOADERS = {
  '/pricing': () => import('./site/pages/PricingPage.jsx'),
  '/how-it-works': () => import('./site/pages/HowItWorks.jsx'),
  '/waitlist': () => import('./site/pages/WaitlistPage.jsx'),
  '/privacy': () => import('./components/Privacy.jsx'),
  '/terms': () => import('./components/Terms.jsx'),
  '/blog': () => import('./site/pages/BlogPages.jsx').then((m) => ({ default: m.BlogIndexPage })),
};

/** Trailing-slash-insensitive, matching App.jsx's own `clean` normalization. */
export const cleanPath = (p) => (p || '/').replace(/\/$/, '') || '/';

/** path → () => import(), or null when the route needs no dynamic chunk. */
export function loaderFor(path) {
  const clean = cleanPath(path);
  if (CORE_LOADERS[clean]) return CORE_LOADERS[clean];
  if (MARKETING_LOADERS[clean]) return MARKETING_LOADERS[clean];
  // Every post shares one component; the slug is passed as a prop by App.jsx.
  if (clean.startsWith('/blog/')) return () => import('./site/pages/BlogPages.jsx').then((m) => ({ default: m.BlogPostPage }));
  // Same arrangement for the C3 intent library. Without a loader here the
  // prerender emits a head-only page and the body — which is the entire SEO
  // payload — never reaches the crawler.
  if (clean.startsWith('/how-to/')) return () => import('./components/IntentPage.jsx');
  return null;
}

const eager = new Map();

/** The already-resolved component for `path`, or null to use App.jsx's lazy one. */
export function eagerFor(path) {
  return eager.get(cleanPath(path)) || null;
}

/**
 * Resolve a route's chunk and register it. Returns null when the route has no
 * loader, and swallows import failures so a bad chunk degrades to the lazy path
 * (a Suspense fallback) rather than taking down the whole render.
 */
export async function preloadRoute(path) {
  const load = loaderFor(path);
  if (!load) return null;
  try {
    const mod = await load();
    eager.set(cleanPath(path), mod.default);
    return mod.default;
  } catch {
    return null;
  }
}
